import maplibregl, { type Map as MlMap, type StyleSpecification } from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import styleTemplate from './knn_parchment.json';
import { config } from '../config';
import { materialUrl } from './totem-materials';

let registered = false;

// MapLibre GL JS has no built-in pmtiles:// scheme (unlike the mobile native
// plugin), so register the pmtiles-js protocol handler once before creating any
// map that uses a pmtiles source.
export function registerPmtilesProtocol(): void {
  if (registered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
  registered = true;
}

// buildStyle returns the shared parchment style with the tiles URL injected.
// This is the same style JSON the mobile client ships (mobile/assets/
// map_styles/knn_parchment.json) — the only web-specific step is swapping the
// {TILES_URL} placeholder for the configured pmtiles:// URL.
export function buildStyle(): StyleSpecification {
  const style = structuredClone(styleTemplate) as StyleSpecification;
  const source = style.sources.omt;
  if (source.type === 'vector') {
    source.url = config.tilesUrl;
  }
  return style;
}

// The base style's pattern layers (cobble roads) reference totem material
// images by their runtime names (totem-tex-stone, …) — there is no sprite
// sheet. wireStyleTextures resolves those on demand: MapLibre fires
// styleimagemissing per tile batch, so the handler must be idempotent and
// dedupe in-flight loads. Solid-color road layers sit under the pattern
// layers, so an unresolvable image degrades to flat roads, never missing ones.
export function wireStyleTextures(map: MlMap): void {
  const loading = new Set<string>();
  map.on('styleimagemissing', (e: { id: string }) => {
    const match = /^totem-tex-(.+)$/.exec(e.id);
    if (!match || loading.has(e.id) || map.hasImage(e.id)) return;
    const url = materialUrl(match[1]);
    if (!url) return;
    loading.add(e.id);
    void (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const image = await createImageBitmap(await res.blob());
        if (!map.hasImage(e.id)) map.addImage(e.id, image);
      } catch {
        // Leave the solid fallback layers in place.
      } finally {
        loading.delete(e.id);
      }
    })();
  });
}
